import React, { useContext, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/services';
import { BreakpointTrackerContext } from '@jutro/layout';
import { MockUpUtil } from 'gw-portals-util-js';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import metadata from './PolicyInformationPage.metadata.json5';
import PolicyVehiclesDriversInfo from '../../../nexus-capability-gateway-react/Policies/Summary/pa/PolicyVehiclesDriversInfo/PolicyVehiclesDriversInfo';
import messages from './PolicyInformationPage.messages';
import styles from './PolicyInformationPage.modules.scss';
import {formatCurrency} from '@jutro/components';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import {SubmissionService} from 'gw-capability-gateway';
import { ModalNextProvider, Loader } from '@jutro/components';

function removeInitialData(submissionVM) {
    // eslint-disable-next-line no-param-reassign
    submissionVM.value = MockUpUtil.cleanUpMockedProperties(
        _.get(submissionVM, 'value'),
        'quote.ba',
        'bindData.contactPhone',
        'bindData.contactEmail'
    );
    return submissionVM;
}

function initialiseVM(submissionVM) {
    const vm = removeInitialData(submissionVM);
    vm.bindData.value = vm.bindData.value || {};
    return vm;
}

function PolicyInformationPage(props) {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { wizardData: submissionVM, updateWizardData } = props;
    const localeService = ServiceManager.getService('locale-service');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate } = useValidation('PolicyInformationPage');
    const intl = useContext(IntlContext);

    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };

    useEffect(() => {

        let vm = viewModelService.clone(submissionVM);
        vm = initialiseVM(vm);
        if (!_.isEqual(vm.value, submissionVM.value)) {
            updateWizardData(vm);
        }

        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = useCallback(async () => {
        /*submissionVM.value = await LoadSaveService.updateQuotedSubmission(
            submissionVM.value,
            authHeader
        );*/
        return submissionVM;
    }, [LoadSaveService, authHeader, submissionVM]);

    const generateQuoteDocument = useCallback(
        () => {
            SubmissionService.fetchQuoteDocument(submissionVM.value.quoteID, submissionVM.baseData.isIssuanceJob.value, authHeader).then((response) => {
                if(response.isAvailable){
                    //var downloadUrl = $scope.xCenterDownloadURI + '/' + response.publicID + '?token=' + response.sessionID + '&portalRoute=' + $scope.documentNotFoundURL;
                    //window.open(downloadUrl, '_blank');
                    var downloadUrl = DocumentPageService.downloadDocument(
                        response.publicID,
                        response.sessionID
                    );
                    window.open(downloadUrl, '_blank');
                } else {
                    ModalNextProvider.showAlert({
                        title: messages.baDocumentGenerationInProgress,
                        message: messages.baDocumentGenerationInProgressConf,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: 'Ok'
                    }).catch(_.noop);
                }
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.baDocumentGenerationError,
                    message: messages.baDocumentGenerationErrorConf,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: 'Ok'
                }).catch(_.noop);
            });
        },
        [submissionVM, authHeader, DocumentPageService]
    );

    const renderCellContent = useCallback((data, index, tableProps) => {
        const { path } = tableProps;
        if (path === 'licenseState') {
            return translator({
                id: `typekey.State.${data.licenseState}`,
                defaultMessage: data.licenseState
            });
        }
        return _.get(data, path);
    },
    [translator]);

    const policyVehicleInfoData = () => {
        let submissionVMCopy = viewModelService.clone(submissionVM);
        let vehicleData = _.get(submissionVMCopy, 'lobData.businessAuto.coverables.vehicles.value')
        vehicleData = vehicleData.map((v)=> {

            const garagedAt = submissionVMCopy.baseData.policyLocations.value.filter((l) => {
                return l.publicID == v['garagedAt']
            })[0];

            v['annualKilometers'] = v['annualMileage'];
            v['vicccode'] = v['viccCode'];
            v['garagedAt'] = garagedAt ? garagedAt.displayName :  v['garagedAt'];
            v['primaryUse'] = translator({id: 'typekey.VehiclePrimaryUse.' +  v['primaryUse'], defaultMessage: v['primaryUse']})

            v['coverages'] = submissionVMCopy.lobData.businessAuto.offerings.children[0].coverages.vehicleCoverages.value.filter((vc)=> {
                return vc.fixedId == v.fixedId
            })[0].coverages.filter((c)=>{
                return c.selected
            });

            v['discounts'] = submissionVMCopy.lobData.businessAuto.offerings.children[0].coverages.vehicleCoverages.value.filter((vc)=> {
                return vc.fixedId == v.fixedId
            })[0].discounts.filter((c) => {
                return c;
            });

            v['drivers'] = submissionVMCopy.lobData.businessAuto.coverables.vehicleDrivers.value.filter((vd)=> { return vd.vehicleID == v.fixedId}).map((d)=> {
                return {
                displayName: d.driverName,
                driverType: translator({id: 'typekey.DriverType_EY.' + d.driverType}),
                premium: d.premium
                }
            });

            return v
        })
        return  {
            vehicleDTOs: vehicleData,
            driverDTOs: _.get(submissionVMCopy, 'lobData.businessAuto.coverables.drivers.value'),
            coverageDTOs: []
        }
    }

    const policyDetailsAccordion = () => {
        const periodStartDate = _.get(submissionVM, 'baseData.periodStartDate.value');
        const periodEndDate = _.get(submissionVM, 'baseData.periodEndDate.value')
        const taxAndFees =  _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.fees_EY.amount.value') + _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.taxes.amount.value')
        return {
            effectiveDate: intl.formatDate(new Date(periodStartDate.year, periodStartDate.month, periodStartDate.day), { year: 'numeric', month: 'short', day: 'numeric' }),
            expirationDate: intl.formatDate(new Date(periodEndDate.year, periodEndDate.month, periodEndDate.day), { year: 'numeric', month: 'short', day: 'numeric' }),
            producerCode: _.get(submissionVM, 'baseData.producerCode.value'),
            periodStatus:   _.get(submissionVM, 'baseData.periodStatus.value'),
            totalPremium: formatCurrency(intl, { value: _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.totalBeforeTaxes.amount.value'), currency: 'cad', showFractions: true, currencyDisplay: 'symbol' }),
            premiumfees: formatCurrency(intl, { value: _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.fees_EY.amount.value'), currency: 'cad', showFractions: true, currencyDisplay: 'symbol' }),
            premiumtax: formatCurrency(intl, { value: _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.taxes.amount.value'), currency: 'cad', showFractions: true, currencyDisplay: 'symbol' }),
            totalCost: formatCurrency(intl, { value: _.get(submissionVM, 'quoteData.offeredQuotes.children[0].premium.total.amount.value'), currency: 'cad', showFractions: true, currencyDisplay: 'symbol' }),
        }
    }

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: 'top'
        },
        phoneNumber: {
            countryCode: localeService.getDefaultPhoneCountry()
        },
        policyVehicleInfo: {
            value: policyVehicleInfoData(),
            isPolicyInfoPage: true
        },
        effectiveDate: {
            value: policyDetailsAccordion().effectiveDate
        },
        expirationDate: {
            value: policyDetailsAccordion().expirationDate
        },
        producerCode: {
            value: policyDetailsAccordion().producerCode
        },
        totalPremium: {
            value: policyDetailsAccordion().totalPremium,
            labelPosition: 'left'
        },
        totalCost: {
            value: policyDetailsAccordion().totalCost,
            labelPosition: 'left'
        },
        premiumfees:{
            value: policyDetailsAccordion().premiumfees,
            labelPosition: 'left'
        },
        premiumtax: {
            value: policyDetailsAccordion().premiumtax,
            labelPosition: 'left'
        },
        periodStatus: {
            value: translator({id: policyDetailsAccordion().periodStatus.name })
        }

    };

    const resolvers = {
        resolveCallbackMap: {
            onRenderCell: renderCellContent,
            generateQuoteDocument: generateQuoteDocument
        },
        resolveComponentMap: {
            policypacomponent: PolicyVehiclesDriversInfo,
        }
    };

    return (
        <WizardPage onNext={onNext} disableNext={!isComponentValid}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                //model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

PolicyInformationPage.propTypes = wizardProps;
export default PolicyInformationPage;
